import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {
  Checkbox,
  MenuItem,
  Stack,
  Typography,
  useTheme,
  alpha,
  darken,
  Box,
} from '@mui/material';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { bloxValues } from 'themes/constants';
import { SelectMenuItemProps } from 'components/Inputs/Dropdown/SelectDropdown/SelectMenuItem';
import {
  getCheckboxSubTypoSx,
  getCheckboxSx,
  getCheckboxTypoSx,
} from 'components/Inputs/Dropdown/dropdownConsts';
import { useBatchEdit } from '../context';

export const BatchEditSelectMenuItem = memo((props: SelectMenuItemProps) => {
  const {
    inputLabel,
    choice,
    intersectionRef,
    isDataPrivate,
    handleSelectionChange,
    multiple,
    group,
  } = props;
  const { t } = useTranslation();
  const { cycleSelectionState = {} } = props;
  const { addRemoveValues } = useBatchEdit();
  const theme = useTheme();

  const checked =
    group && choice
      ? (() => {
          const item = addRemoveValues[group]?.find(
            (item: any) => item.value === choice.value,
          );
          if (!item) return false;
          if (!item.misc?.established) return item.misc?.action === 'add';
          return item.misc.action !== 'remove';
        })()
      : false;

  const getAction = useCallback(
    (choice: any) => {
      const fromCycle = cycleSelectionState[choice.value];
      if (fromCycle) {
        // Return if using cycle selection state
        return fromCycle;
      }
      if (group) {
        const data = addRemoveValues[group]?.find(
          (item: any) => item.value === choice.value,
        );
        if (data?.misc?.action) return data.misc.action;
        if (data?.misc?.established) return 'keep';
      }
      return undefined;
    },
    [addRemoveValues, group, cycleSelectionState],
  );

  const getBackgroundColor = useCallback(
    (choice: any) => {
      const action = getAction(choice);
      const base = {
        add: theme.palette.success.main,
        remove: theme.palette.error.main,
        keep: theme.palette.info.main,
      }[action as string];
      return base ? alpha(base, 0.25) : theme.palette.background.paper;
    },
    [getAction, theme],
  );

  const getHoverColor = useCallback(
    (choice: any) => {
      const color = getBackgroundColor(choice);
      return alpha(darken(color, 0.25), 0.25);
    },
    [getBackgroundColor],
  );

  const getActionText = useCallback(
    (choice: any) =>
      ({
        add: t('blox.actions.add'),
        remove: t('blox.actions.remove'),
        keep: t('blox.actions.keep'),
      })[getAction(choice) as string] || '',
    [getAction, t],
  );

  // Handle section headers
  if (choice?.__isHeader) {
    return (
      <MenuItem
        disabled
        divider
        sx={{
          p: 1,
          fontWeight: bloxValues.fontWeightMedium,
          opacity: 0.8,
          fontSize: bloxValues.smallFontSizeRem,
        }}
      >
        {choice.label}
      </MenuItem>
    );
  }

  return (
    <MenuItem
      ref={intersectionRef}
      value={choice?.value}
      onClick={(event) => handleSelectionChange(event, choice.value)}
      disableGutters
      disableRipple
      sx={{
        backgroundColor: getBackgroundColor(choice),
        '&:hover': {
          backgroundColor: getHoverColor(choice),
        },
        '& .Mui-focusVisible': {
          backgroundColor: theme.palette.action.focus,
        },
        minHeight: 'auto',
        alignItems: 'flex-start',
        [theme.breakpoints.down('sm')]: {
          minHeight: 'auto',
        },
      }}
    >
      <Checkbox
        size="small"
        data-private={isDataPrivate || undefined}
        icon={
          multiple ? (
            <CheckBoxOutlineBlankIcon color="info" />
          ) : (
            <RadioButtonUncheckedIcon color="info" />
          )
        }
        checkedIcon={
          multiple ? (
            <CheckBoxIcon color="primary" />
          ) : (
            <RadioButtonCheckedIcon color="primary" />
          )
        }
        disableRipple
        aria-multiselectable
        aria-checked={checked}
        checked={checked}
        value={choice?.value}
        name={`${inputLabel}-${multiple ? 'checkbox' : 'radio'}`}
        sx={getCheckboxSx}
        slotProps={{
          input: {
            'aria-label': `${choice?.label}-${multiple ? 'checkbox' : 'radio'}`,
          },
        }}
      />
      <Stack
        direction="column"
        sx={{
          width: '100%',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: 0.5,
            pr: 1,
          }}
        >
          <Box>
            <Typography
              data-private={isDataPrivate || undefined}
              sx={getCheckboxTypoSx}
            >
              {choice?.label}
            </Typography>
            {choice?.sublabel && (
              <Typography
                data-private={isDataPrivate || undefined}
                sx={getCheckboxSubTypoSx}
              >
                {choice.sublabel}
              </Typography>
            )}
          </Box>
          <Box>
            <Typography
              data-private={isDataPrivate || undefined}
              sx={getCheckboxTypoSx}
            >
              {getActionText(choice)}
            </Typography>
          </Box>
        </Box>
      </Stack>
    </MenuItem>
  );
});

BatchEditSelectMenuItem.displayName = 'BatchEditSelectMenuItem';
